/**
 * @file Baked point lights for the NIGHT channel. Lit windows, braziers, neon,
 * streetlamps — anything that should make the ground under it glow after dusk.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NO SHADOW RAYS
 * ─────────────────────────────────────────────────────────────────────────────
 * The night channel is a WASH, not a lighting solution: emitters exist so alleys
 * are not uniformly dead and so the street under a lit tower reads warm. Tracing
 * visibility would multiply the bake cost by the emitter count for a difference
 * nobody sees at 52 degrees of pitch under bloom. The AO factor the bake already
 * has does the occlusion work (`contribution * ao`).
 *
 * Falloff is `(1 - d/range)^2` — a windowed inverse-square. It reaches EXACTLY
 * zero at `range`, which is what lets the spatial bucket use `range` as a hard
 * cutoff without a visible seam (true `1/d^2` never reaches zero and pops).
 *
 * ZERO three.js imports: this module is part of `bake.worker.js`'s graph.
 *
 * @module engine/bake/Emitters
 */

/** @typedef {import('../types.js').Emitter} Emitter */

export class EmitterSet {
  constructor() {
    /** @type {number[]} 8 per emitter: x,y,z, r,g,b, range, intensity */
    this._raw = [];
    this._packed = false;
    this.cell = 8;

    /** @type {Float32Array} */
    this._data = new Float32Array(0);
    /** @type {Int32Array} */
    this._cellStart = new Int32Array(1);
    /** @type {Int32Array} */
    this._cellItems = new Int32Array(0);
    this._count = 0;
    this._nx = 1;
    this._nz = 1;
    this._minX = 0;
    this._minZ = 0;
  }

  /** @returns {number} */
  get count() {
    return this._packed ? this._count : this._raw.length / 8;
  }

  /**
   * @param {number} x @param {number} y @param {number} z
   * @param {[number,number,number]|{r:number,g:number,b:number}} colorRGB Linear
   *   RGB; may exceed 1 (bloom fuel).
   * @param {number} range Metres. Hard cutoff — contribution is 0 beyond it.
   * @param {number} intensity
   * @returns {this}
   */
  add(x, y, z, colorRGB, range, intensity = 1) {
    const r = Array.isArray(colorRGB) ? colorRGB[0] : colorRGB.r;
    const g = Array.isArray(colorRGB) ? colorRGB[1] : colorRGB.g;
    const b = Array.isArray(colorRGB) ? colorRGB[2] : colorRGB.b;
    if (!(range > 0) || !(intensity > 0)) return this;
    this._raw.push(x, y, z, r, g, b, range, intensity);
    this._packed = false;
    return this;
  }

  /**
   * Adds a {@link Emitter} record (the `types.js` shape). `dir`/`spread` are
   * accepted but ignored — see the module header; cone narrowing is a look the
   * emissive window shells already provide at zero bake cost.
   * @param {Emitter} e
   * @returns {this}
   */
  addEmitter(e) {
    return this.add(e.x, e.y, e.z, e.color, e.range, e.intensity ?? 1);
  }

  /**
   * @param {Emitter[]} list
   * @returns {this}
   */
  addAll(list) {
    for (const e of list || []) if (e) this.addEmitter(e);
    return this;
  }

  /**
   * Buckets emitters into a `cell`-metre XZ grid. An emitter is inserted into
   * every cell its RANGE touches, so `gather` only ever looks at one cell.
   * @param {{cell?: number}} [opts]
   * @returns {this}
   */
  build({ cell = 8 } = {}) {
    const n = this._raw.length / 8;
    this.cell = Math.max(1, cell);
    this._count = n;
    this._packed = true;
    this._data = new Float32Array(this._raw);

    if (n === 0) {
      this._cellStart = new Int32Array(2);
      this._cellItems = new Int32Array(0);
      this._nx = 1;
      this._nz = 1;
      this._minX = 0;
      this._minZ = 0;
      return this;
    }

    const D = this._data;
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < n; i++) {
      const o = i * 8;
      const r = D[o + 6];
      if (D[o] - r < minX) minX = D[o] - r;
      if (D[o] + r > maxX) maxX = D[o] + r;
      if (D[o + 2] - r < minZ) minZ = D[o + 2] - r;
      if (D[o + 2] + r > maxZ) maxZ = D[o + 2] + r;
    }
    const c = this.cell;
    this._minX = Math.floor(minX / c) * c;
    this._minZ = Math.floor(minZ / c) * c;
    this._nx = Math.max(1, Math.ceil((maxX - this._minX) / c));
    this._nz = Math.max(1, Math.ceil((maxZ - this._minZ) / c));

    const nCells = this._nx * this._nz;
    const counts = new Int32Array(nCells);
    const span = (i) => {
      const o = i * 8;
      const r = D[o + 6];
      return [
        this._gx(D[o] - r), this._gx(D[o] + r),
        this._gz(D[o + 2] - r), this._gz(D[o + 2] + r),
      ];
    };

    for (let i = 0; i < n; i++) {
      const [x0, x1, z0, z1] = span(i);
      for (let gz = z0; gz <= z1; gz++) for (let gx = x0; gx <= x1; gx++) counts[gz * this._nx + gx]++;
    }
    const start = new Int32Array(nCells + 1);
    let acc = 0;
    for (let i = 0; i < nCells; i++) {
      start[i] = acc;
      acc += counts[i];
    }
    start[nCells] = acc;
    const items = new Int32Array(acc);
    const cursor = start.slice(0, nCells);
    for (let i = 0; i < n; i++) {
      const [x0, x1, z0, z1] = span(i);
      for (let gz = z0; gz <= z1; gz++) {
        for (let gx = x0; gx <= x1; gx++) items[cursor[gz * this._nx + gx]++] = i;
      }
    }

    this._cellStart = start;
    this._cellItems = items;
    return this;
  }

  /** @param {number} x @returns {number} */
  _gx(x) {
    const i = Math.floor((x - this._minX) / this.cell);
    return i < 0 ? 0 : i >= this._nx ? this._nx - 1 : i;
  }

  /** @param {number} z @returns {number} */
  _gz(z) {
    const i = Math.floor((z - this._minZ) / this.cell);
    return i < 0 ? 0 : i >= this._nz ? this._nz - 1 : i;
  }

  /**
   * Sums every emitter reaching `(px,py,pz)`:
   * `color * intensity * max(dot(n, dir), 0) * (1 - d/range)^2`.
   *
   * @param {number} px @param {number} py @param {number} pz
   * @param {number} nx @param {number} ny @param {number} nz Surface normal.
   *   Pass `0,0,0` for an OMNIDIRECTIONAL gather (light probes have no surface).
   * @param {Float32Array|number[]} outRGB Length >= 3. Overwritten, not summed.
   * @returns {Float32Array|number[]} `outRGB`
   */
  gather(px, py, pz, nx, ny, nz, outRGB) {
    outRGB[0] = 0;
    outRGB[1] = 0;
    outRGB[2] = 0;
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return outRGB;

    const gx = Math.floor((px - this._minX) / this.cell);
    const gz = Math.floor((pz - this._minZ) / this.cell);
    if (gx < 0 || gz < 0 || gx >= this._nx || gz >= this._nz) return outRGB;

    const ci = gz * this._nx + gx;
    const start = this._cellStart[ci];
    const end = this._cellStart[ci + 1];
    const D = this._data;
    const omni = nx * nx + ny * ny + nz * nz < 1e-12;

    for (let k = start; k < end; k++) {
      const o = this._cellItems[k] * 8;
      const dx = D[o] - px;
      const dy = D[o + 1] - py;
      const dz = D[o + 2] - pz;
      const d2 = dx * dx + dy * dy + dz * dz;
      const range = D[o + 6];
      if (d2 >= range * range) continue;
      const d = Math.sqrt(d2);

      let ndl = 1;
      if (!omni) {
        if (d < 1e-6) ndl = 1;
        else {
          ndl = (nx * dx + ny * dy + nz * dz) / d;
          if (ndl <= 0) continue;
        }
      }

      const f = 1 - d / range;
      const w = f * f * ndl * D[o + 7];
      outRGB[0] += D[o + 3] * w;
      outRGB[1] += D[o + 4] * w;
      outRGB[2] += D[o + 5] * w;
    }
    return outRGB;
  }

  // ── worker transport ───────────────────────────────────────────────────────

  /**
   * Plain typed arrays for `postMessage`. Structured-clone them (do NOT transfer)
   * — the main thread keeps using this set for the light probes.
   * @returns {Object}
   */
  toArrays() {
    if (!this._packed) this.build({ cell: this.cell });
    return {
      cell: this.cell,
      count: this._count,
      nx: this._nx,
      nz: this._nz,
      minX: this._minX,
      minZ: this._minZ,
      data: this._data,
      cellStart: this._cellStart,
      cellItems: this._cellItems,
    };
  }

  /**
   * @param {ReturnType<EmitterSet['toArrays']>|null|undefined} o
   * @returns {EmitterSet|null}
   */
  static fromArrays(o) {
    if (!o) return null;
    const s = new EmitterSet();
    s._packed = true;
    s.cell = o.cell;
    s._count = o.count;
    s._nx = o.nx;
    s._nz = o.nz;
    s._minX = o.minX;
    s._minZ = o.minZ;
    s._data = o.data;
    s._cellStart = o.cellStart;
    s._cellItems = o.cellItems;
    return s;
  }
}
